import { lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { TimeRequiredDialog } from "@/components/TimeRequiredDialog";
import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";
import VaultGate from "@/components/VaultGate";
import MyDay from "@/pages/MyDay";

// Pages load on demand; all chunks are prefetched once the browser is idle so navigation stays instant.
const LOADERS = {
  Whiteboard: () => import("@/pages/Whiteboard"),
  MyTasks: () => import("@/pages/MyTasks"),
  DailyLog: () => import("@/pages/DailyLog"),
  Team: () => import("@/pages/Team"),
  Reports: () => import("@/pages/Reports"),
  TeamLogs: () => import("@/pages/TeamLogs"),
  Backlog: () => import("@/pages/Backlog"),
  HotLeads: () => import("@/pages/HotLeads"),
  SocialCalendar: () => import("@/pages/SocialCalendar"),
  BookingsCalendar: () => import("@/pages/BookingsCalendar"),
  Projects: () => import("@/pages/Projects"),
  Finance: () => import("@/pages/Finance"),
  WeeklyReports: () => import("@/pages/WeeklyReports"),
  ShootsCalendar: () => import("@/pages/ShootsCalendar"),
  NewShoot: () => import("@/pages/NewShoot"),
  EquipmentAdmin: () => import("@/pages/EquipmentAdmin"),
  StudioBookingSlip: () => import("@/pages/StudioBookingSlip"),
  Alarms: () => import("@/pages/Alarms"),
  MonthlyReport: () => import("@/pages/MonthlyReport"),
  MonthlyTargets: () => import("@/pages/MonthlyTargets"),
  Leaves: () => import("@/pages/Leaves"),
};
const P = Object.fromEntries(Object.entries(LOADERS).map(([k, f]) => [k, lazy(f)]));
const { Whiteboard, MyTasks, DailyLog, Team, Reports, TeamLogs, Backlog, HotLeads, SocialCalendar, BookingsCalendar, Projects, Finance, WeeklyReports, ShootsCalendar, NewShoot, EquipmentAdmin, StudioBookingSlip, Alarms, MonthlyReport, MonthlyTargets, Leaves } = P;
if (typeof window !== "undefined") {
  const prefetch = () => Object.values(LOADERS).forEach((f) => f().catch(() => {}));
  window.addEventListener("load", () => (window.requestIdleCallback ? window.requestIdleCallback(prefetch, { timeout: 4000 }) : setTimeout(prefetch, 2500)));
}

function Private({ children, managerOnly = false, superAdminOnly = false }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-white/60 font-mono text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (superAdminOnly && !user.is_super_admin) return <Navigate to="/" replace />;
  if (managerOnly && user.role !== "manager" && !user.is_super_admin) return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/my-day" replace />;
  return children;
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" offset={72} richColors />;
}

export default function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <ThemedToaster />
      <TimeRequiredDialog />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route element={<Private><AppLayout /></Private>}>
            <Route path="/" element={<Whiteboard />} />
            <Route path="/my-day" element={<MyDay />} />
            <Route path="/monthly-targets" element={<MonthlyTargets />} />
            <Route path="/leaves" element={<Leaves />} />
            <Route path="/my-tasks" element={<MyTasks />} />
            <Route path="/daily-log" element={<DailyLog />} />
            <Route path="/team" element={<Private superAdminOnly><Team /></Private>} />
            <Route path="/backlog" element={<Private managerOnly><Backlog /></Private>} />
            <Route path="/team-logs" element={<Private managerOnly><TeamLogs /></Private>} />
            <Route path="/reports" element={<Private><Reports /></Private>} />
            <Route path="/hot-leads" element={<Private><VaultGate name="hot_leads" label="Hot Leads"><HotLeads /></VaultGate></Private>} />
            <Route path="/social-calendar" element={<Private><SocialCalendar /></Private>} />
            <Route path="/bookings-calendar" element={<Private><BookingsCalendar /></Private>} />
            <Route path="/projects" element={<Private><Projects /></Private>} />
            <Route path="/finance" element={<Private><VaultGate name="finance" label="Finance"><Finance /></VaultGate></Private>} />
            <Route path="/weekly-reports" element={<Private><WeeklyReports /></Private>} />
            <Route path="/shoots" element={<Private><ShootsCalendar /></Private>} />
            <Route path="/shoots/new" element={<Private><NewShoot /></Private>} />
            <Route path="/shoots/:id/edit" element={<Private><NewShoot /></Private>} />
            <Route path="/shoots/equipment" element={<Private><EquipmentAdmin /></Private>} />
            <Route path="/shoots/booking-slip" element={<Private><StudioBookingSlip /></Private>} />
            <Route path="/shoots/booking-slip/:id" element={<Private><StudioBookingSlip /></Private>} />
            <Route path="/alarms" element={<Private><Alarms /></Private>} />
            <Route path="/monthly-report" element={<Private><MonthlyReport /></Private>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}
